import {Injectable} from '@angular/core';
import {AuthService} from './auth.service';
import {AnyMap} from '../util/interfaces';
import {Observable, of, Subject, timer} from 'rxjs';
import {AbstractControl} from '@angular/forms';
import {Constants} from '../util/constants';
import {debounceTime, distinctUntilChanged, map, share, switchMap, take} from 'rxjs/operators';
import {PromoService} from "./promo.service";

type AsyncBooleanCheckFn = (value: string) => Observable<boolean>;
type FieldValidatorFn = (control: AbstractControl) => Observable<AnyMap>;
type ToggleFn = (flag: boolean) => void;

/** Generates various async validators
 */
@Injectable({
    providedIn: 'root'
})
export class AsyncValidatorFactoryService {

    constructor(private authService: AuthService,
                private promoService: PromoService) {
    }

    /** Creates and returns an async validator function that checks if the text in the target control corresponds to
     * an email address that can be used for a new user (not used by any active user) or not (already in use)
     *
     * @param showBusy (optional) callback to be notified when server requests are being performed and completed
     * @param oldEmail (optional) previous email value known to be available, which will cause the server check to be bypassed
     */
    getEmailAvailableValidator(showBusy?: ToggleFn, oldEmail?: string): FieldValidatorFn {
        return this.getValueAvailableValidator(this.authService.isEmailAvailable.bind(this.authService),
                                    'emailTaken', showBusy, oldEmail);
    }

    /** Creates and returns an async validator function that checks if the text in the target control corresponds to
     * a code that can be used for a new promo (not used by any active promo) or not (already in use)
     *
     * @param showBusy (optional) callback to be notified when server requests are being performed and completed
     * @param oldCode (optional) previous code value known to be available, which will cause the server check to be bypassed
     */
    getPromoCodeAvailableValidator(showBusy?: ToggleFn, oldCode?: string): FieldValidatorFn {
        return this.getValueAvailableValidator(this.promoService.checkAvailable.bind(this.promoService),
                                     'codeTaken', showBusy, oldCode);
    }

    /** Creates and returns an async validator function that checks if the text in the target control is available for
     * use as a new unique value assigned to some persistent entity, according to some async service call provided
     * to check for this availability. The resulting validator will return either null if the value is still available
     * or an object with property <resultLabel> and value true if not available.
     *
     * @param checkAvailable async function that checks whether the parameter it's passed is available for use
     * @param resultLabel the property name to be used in the result object by the validator when value is
     *         unavailable: {<resultLabel>: true}  (and null when still available)
     * @param showBusy (optional) callback to be notified when server requests are being performed and completed
     * @param oldValue (optional) previous value known to be available, which will cause the async check to be bypassed
     * @return a function that can be used as a form field validator
     */
    private getValueAvailableValidator(checkAvailable: AsyncBooleanCheckFn, resultLabel: string,
                                       showBusy?: ToggleFn, oldValue?: string): FieldValidatorFn {
        // This will emit values from the control
        let validatorInput = new Subject<string>();
        // This is the observable chain that will process such control values and emit null if email is available, or {emailTaken: true}
        // otherwise
        let validatorChain = validatorInput.pipe(
            // Don't check right away if user is still typing
            debounceTime(Constants.DEFAULT_DEBOUNCE_DELAY),
            // Don't emit anything new if content doesn't change
            distinctUntilChanged(),
            // Call the actual service to see if email is available
            switchMap((value: string) => {
                if (showBusy)
                    showBusy(true);
                // But if we have a previous value known to be available, skip the service call
                if (oldValue && oldValue === value)
                    return of(true);
                return checkAvailable(value);
            }),
            // Convert the service response to proper format
            map((available) => {
                if (showBusy)
                    showBusy(false);
                if (available)
                    return null
                let result = {};
                result[resultLabel] = true;
                return result;
            }),
            // Use single source for potentially multiple subscribers, so not all of them trigger independent service calls
            share(),
            // Get the first emitted value as the result and be done
            take(1));

        // Return the actual validator function that uses the above
        return (control: AbstractControl): Observable<AnyMap> => {
            timer().subscribe(() => {
                validatorInput.next(control.value);
            });
            return validatorChain;
        }
    }
}
